import type { PropType, VNode } from "vue";
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { VBottomSheet, VIcon, VSheet } from "vuetify/lib";
import BmBtnDropdown from "@/components/controls/input/BmBtnDropdown.vue";
import BmBtn from "@/components/controls/input/BmBtn.vue";
import { isNotNullOrUndefined } from "@/app/apiutils";
import Guid from "@/app/types/common/guid";
import "./buttonBar.sass";
import type {
  ButtonBarConfiguration,
  ButtonConfiguration,
  MoreButtonConfiguration,
} from "@/components/platform/components/buttonbar/buttonBarConfiguration";
import {
  isActionButtonConfiguration,
  isMoreButtonConfiguration,
  isRoutableButtonConfiguration,
} from "@/components/platform/components/buttonbar/buttonBarConfiguration";
import { Portal } from "portal-vue";
import { resolveDisplayText } from "@/components/platform/common/displayText";
import { resolveLabellingConfigurationToControlProps } from "@/components/platform/common/labelling";
import { isComponentVisible } from "@/components/platform/components/form/controlConfiguration";
import type { BmBtnDropdownMenuItem } from "@/components/controls/input/types/BmBtnDropdownTypes";

type PortalName = "footer";

export function renderButtonBar(
  buttonBarConfiguration: ButtonBarConfiguration,
  portalTo?: PortalName
): VNode {
  const buttonBar = h(ButtonBar, {
    props: {
      configuration: buttonBarConfiguration,
    },
  });

  if (portalTo) {
    return h(
      Portal,
      {
        props: {
          to: portalTo,
        },
      },
      [buttonBar]
    );
  }

  return buttonBar;
}

/**
 * Creates a "bar of buttons" that automatically re-arrange depending on the
 * screen width available.
 *
 * If there is not enough space to render the full bar then it will be
 * collapsed according to the supplied collapse strategy.
 */
export const ButtonBar = defineComponent({
  name: "ButtonBar",
  props: {
    configuration: {
      type: Object as PropType<ButtonBarConfiguration>,
      required: true,
    },
  },
  setup(props) {
    const showBottomSheet = ref(false);

    const rootElementId = Guid.create().toString();

    const desiredWidthPixels = ref(10000);

    onMounted(() => {
      // Vue2 composition api does not support refs in setup render functions.
      // TODO update when we migrate to Vue 3
      const rootElement = document.getElementById(rootElementId)!;

      const ro = new ResizeObserver((entries) => {
        desiredWidthPixels.value = entries[0]?.contentRect.width;
      });

      ro.observe(rootElement);
    });

    const minWidthDesktopMode = computed(() => {
      return (
        100 +
        props.configuration.leftButtons
          .concat(props.configuration.rightButtons)
          .filter((bo) => isComponentVisible(bo))
          .reduce((sum, b) => {
            const numberOfChars = resolveDisplayText(b.label).length;

            // Buttons are roughly 64 pixels without content, and each character after the third adds about 10 pixels.
            return sum + 64 + Math.abs(numberOfChars - 3) * 10;
          }, 0)
      );
    });

    const isCollapsed = computed(() => {
      const hasMoreThanTwoButtons =
        props.configuration.rightButtons.length +
          props.configuration.leftButtons.length >
        2;

      // Revert to collapsed mode if we do not have enough space to render in "desktop mode"
      return (
        hasMoreThanTwoButtons &&
        desiredWidthPixels.value < minWidthDesktopMode.value
      );
    });

    function render(
      leftButtonGroupNodes: VNode[],
      rightButtonGroupNodes: VNode[],
      sheetNodes: VNode[]
    ) {
      return h(
        VBottomSheet,
        {
          props: {
            value: showBottomSheet.value,
          },
          on: {
            input: (val: boolean) => {
              showBottomSheet.value = val;
            },
          },
          scopedSlots: {
            activator: () => {
              return h(
                "div",
                {
                  attrs: { id: rootElementId },
                  class: "d-flex justify-space-between",
                },
                [
                  h("div", [leftButtonGroupNodes]),
                  h("div", [rightButtonGroupNodes]),
                ]
              );
            },
          },
        },
        [
          h(VSheet, { class: "button-bar" }, [
            h(
              "div",
              {
                class: "sheet-container flex",
              },
              sheetNodes
            ),
          ]),
        ]
      );
    }

    return () => {
      let leftButtonGroupNodes: VNode[] = [];
      let rightButtonGroupNodes: VNode[] = [];
      let sheetNodes: VNode[] = [];

      // ** DESKTOP MODE **
      if (!isCollapsed.value) {
        leftButtonGroupNodes = props.configuration.leftButtons
          .map((buttonOptions, idx) =>
            buildButton(buttonOptions, idx !== 0 ? "ml-2" : "")
          )
          .filter(isNotNullOrUndefined);

        // Reverse to make first added right button the last of the array
        const rightButtonOptionsReversed = [
          ...props.configuration.rightButtons,
        ].reverse();

        rightButtonGroupNodes = rightButtonOptionsReversed
          .map((buttonOptions, idx) =>
            buildButton(
              buttonOptions,
              idx !== rightButtonOptionsReversed.length - 1 ? "mr-2" : ""
            )
          )
          .filter(isNotNullOrUndefined);

        return render(leftButtonGroupNodes, rightButtonGroupNodes, sheetNodes);
      }

      // ** ACTION BUTTON MODE **
      if (props.configuration.collapseStrategy.type === "action_button") {
        const allButtons = [
          ...props.configuration.rightButtons.filter(isNotNullOrUndefined),
          ...props.configuration.leftButtons
            .flatMap((b) => (isMoreButtonConfiguration(b) ? b.buttons : b))
            .filter(isNotNullOrUndefined)
            .reverse(),
        ];

        leftButtonGroupNodes = [
          buildButton({
            buttons: allButtons,
            label: props.configuration.collapseStrategy.collapsedButtonLabel,
            type: "secondary",
          } as MoreButtonConfiguration),
        ].filter(isNotNullOrUndefined);

        return render(leftButtonGroupNodes, rightButtonGroupNodes, sheetNodes);
      }

      // ** SHEET MODE **
      sheetNodes = [
        ...props.configuration.rightButtons
          .slice(1)
          .flatMap((b) => (isMoreButtonConfiguration(b) ? b.buttons : b))
          .map((buttonOptions: ButtonConfiguration) =>
            buildButton(buttonOptions, undefined, () => {
              showBottomSheet.value = false;
            })
          )
          .filter(isNotNullOrUndefined),
        ...props.configuration.leftButtons
          .flatMap((b) => (isMoreButtonConfiguration(b) ? b.buttons : b))
          .map((buttonOptions: ButtonConfiguration) =>
            buildButton(buttonOptions, undefined, () => {
              showBottomSheet.value = false;
            })
          )
          .filter(isNotNullOrUndefined)
          .reverse(),
      ];

      // Add the cancel button to the bottom of the sheet
      sheetNodes.push(
        h(BmBtn, { attrs: { icon: true, dark: true } }, [
          h(
            VIcon,
            {
              props: {
                light: true,
              },
              on: {
                click: () => (showBottomSheet.value = false),
              },
            },
            "close"
          ),
        ])
      );

      // Put the most important button on screen
      rightButtonGroupNodes = props.configuration.rightButtons
        .slice(0, 1)
        .map((buttonOptions: ButtonConfiguration) =>
          buildButton(buttonOptions, "mr-2")
        )
        .filter(isNotNullOrUndefined);

      // Add a "more" button to the left to access sheet mode
      leftButtonGroupNodes = [
        h(BmBtn, {
          props: {
            label: resolveDisplayText(
              props.configuration.collapseStrategy.collapsedButtonLabel
            ),
            buttonType: "secondary",
            btnClass: "ml-2",
          },
          on: {
            click: () => {
              showBottomSheet.value = true;
            },
          },
        }),
      ];

      return render(leftButtonGroupNodes, rightButtonGroupNodes, sheetNodes);
    };
  },
});

function buildButton(
  buttonConfiguration: ButtonConfiguration,
  classToAdd?: string,
  actionButtonOnClickCallback?: () => void
): VNode | undefined {
  if (!isComponentVisible(buttonConfiguration)) {
    return undefined;
  }

  const labellingControlProps =
    resolveLabellingConfigurationToControlProps(buttonConfiguration);

  const iconName =
    buttonConfiguration.icon instanceof Function
      ? buttonConfiguration.icon()
      : buttonConfiguration.icon;

  const buttonIcon = buttonConfiguration.icon
    ? h(VIcon, [iconName])
    : undefined;

  if (isMoreButtonConfiguration(buttonConfiguration)) {
    return h(
      BmBtnDropdown,
      {
        attrs: {
          btnClass: "mr-2",
          ...labellingControlProps,
          buttonType: buttonConfiguration.type,
        },
        props: {
          top: true,
          footer: true,
          items: buttonConfiguration.buttons
            .filter((item) => isComponentVisible(item))
            .map((buttonOption: ButtonConfiguration): BmBtnDropdownMenuItem => {
              const routeTo = isRoutableButtonConfiguration(buttonOption)
                ? buttonOption.routeTo instanceof Function
                  ? buttonOption.routeTo()
                  : buttonOption.routeTo
                : undefined;

              const iconName =
                buttonOption.icon instanceof Function
                  ? buttonOption.icon()
                  : buttonOption.icon;

              return {
                text: resolveDisplayText(buttonOption.label),
                action: isActionButtonConfiguration(buttonOption)
                  ? (event: PointerEvent) => {
                      buttonOption.onClick(event);
                      actionButtonOnClickCallback?.();
                    }
                  : undefined,
                to: routeTo,
                disabled: buttonOption.isDisabled?.(),
                icon: iconName,
              };
            })
            .filter(isNotNullOrUndefined),
        },
      },
      [labellingControlProps.label]
    );
  }

  const routeTo = isRoutableButtonConfiguration(buttonConfiguration)
    ? buttonConfiguration.routeTo instanceof Function
      ? buttonConfiguration.routeTo()
      : buttonConfiguration.routeTo
    : undefined;

  return h(
    BmBtn,
    {
      props: {
        buttonType: buttonConfiguration.type,
        // apply standard margins if more than one button should be built
        btnClass: `${classToAdd}`,
      },
      attrs: {
        ...labellingControlProps,
        disabled: buttonConfiguration.isDisabled?.(),
        to: routeTo,
      },
      on: {
        click: (e: PointerEvent) => {
          if (isActionButtonConfiguration(buttonConfiguration)) {
            buttonConfiguration.onClick(e);
            actionButtonOnClickCallback?.();
          }
        },
      },
    },
    [buttonIcon, labellingControlProps.label]
  );
}
